<template>
  <div>
    <quote-list-block
      :empty-result-label="i18n.t('/ProductBlocks/EmptyOptionsResultLabel')"
      :title="i18n.t(`/ProductBlocks/${blockType}/Title`)"
      :class="rowClass"
    >
      <template v-if="selectedProducts.length" #header>
        <div class="vat">{{ i18n.t('/Common/Btw') }}</div>
        <template v-if="showPurchasePrice">
          <div class="price price-incl" :data-qa="`${blockType}-price-incl-header`">
            {{ i18n.t('/Common/Verkoopprijs') }}
          </div>
          <div class="price price-excl" :data-qa="`${blockType}-price-excl-header`">
            {{ i18n.t('/Common/Inkoopprijs') }}
          </div>
        </template>
        <template v-else>
          <div
            v-if="showInclPrice"
            class="price price-incl"
            :data-qa="`${blockType}-price-incl-header`"
          >
            {{ i18n.t('/Common/InclBtw') }}
          </div>
          <div
            v-if="showExclPrice"
            class="price price-excl"
            :data-qa="`${blockType}-price-excl-header`"
          >
            {{ i18n.t('/Common/ExclBtw') }}
          </div>
        </template>
      </template>

      <template v-if="selectedProducts.length" #body>
        <li
          v-for="(selectedProduct, index) in selectedProducts"
          :key="index"
          class="row"
          :data-qa="getDataQaTag(selectedProduct)"
        >
          <div class="title">
            <button
              v-if="selectedProduct.AccessorySource == 'SHOPPING_CART'"
              data-qa="shop-basket-info"
              @click="showModal(selectedProduct)"
            >
              <i class="fa-solid fa-shopping-basket"></i>
            </button>
            {{ selectedProduct.Name }}
          </div>
          <div class="vat">{{ formatNumber(selectedProduct.Price.BtwRate) }}%</div>

          <template v-if="showPurchasePrice">
            <div class="price price-excl" data-qa="purchaseprice-excl">
              {{ i18n.n(selectedProduct.Price.PurchaseExcl, 'currency') }}
            </div>
            <div class="price price-incl" data-qa="price-excl">
              {{ i18n.n(selectedProduct.Price.Excl, 'currency') }}
            </div>
          </template>
          <template v-else>
            <div v-if="showExclPrice" class="price price-excl" data-qa="price-excl">
              {{ i18n.n(selectedProduct.Price.Excl, 'currency') }}
            </div>
            <div v-if="showInclPrice" class="price price-incl" data-qa="price-incl">
              {{ i18n.n(selectedProduct.Price.Incl, 'currency') }}
            </div>
          </template>

          <loader v-if="selectedProduct.Loading" />
          <div v-else class="buttons">
            <button
              v-if="isRemovableOrEditable(selectedProduct)"
              data-qa="product-button-edit"
              @click="toggleEditModal(true, selectedProduct)"
            >
              <icon icon="pencil" width="20" />
            </button>
            <button v-else data-qa="product-button-edit" disabled>
              <icon icon="pencil" disabled="disabled" width="20" />
            </button>
            <button
              v-if="
                showEditAndDelete &&
                (!showPurchasePrice || !selectedProduct.CustomPurchasePrice) &&
                !selectedProduct.Forced
              "
              data-qa="product-button-delete"
              @click="removeFromSelected(selectedProduct)"
            >
              <icon icon="bin" width="20" />
            </button>
            <button v-else disabled>
              <icon icon="bin" disabled="disabled" width="20" />
            </button>
          </div>

          <package
            class="packages"
            :item="selectedProduct"
            :part-of-package="partOfPackage(selectedProduct)"
          />
        </li>
      </template>
    </quote-list-block>
    <modal v-if="showInfoModal" :title="'Configurator accessoire'" @close="showInfoModal = false">
      <div>{{ infoModalText }}</div>
    </modal>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import Package from '@/components/generic/presentational/molecules/package/Package.vue'
import QuoteListBlock from '@/components/generic/presentational/molecules/quoteListBlock/QuoteListBlock.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import { mapGetters } from 'vuex'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'

export default {
  components: {
    Icon,
    Package,
    QuoteListBlock,
    Loader,
    Modal,
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    showPurchasePrice: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    blockType: {
      type: String,
      required: true,
    },
  },
  emits: ['toggle-edit-modal', 'remove-from-selected'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showInfoModal: false,
      infoModalText: '',
    }
  },
  computed: {
    ...mapGetters({
      showInclPrice: 'quote/isPriceShowIncl',
      showExclPrice: 'quote/isPriceShowExcl',
    }),
    showEditAndDelete() {
      return this.isEditable
    },
    rowClass() {
      if (this.showPurchasePrice) return ''
      if (!this.showInclPrice) return 'row-price-excl'
      if (!this.showExclPrice) return 'row-price-incl'
      return ''
    },
  },
  methods: {
    showModal(selectedProduct) {
      this.infoModalText = `Accessoire '${selectedProduct.Name}' is toegevoegd via de configurator`
      this.showInfoModal = true
    },
    getDataQaTag(selectedProduct) {
      if (selectedProduct.IsPCQMGeneratedId) return 'is-custom-product'
      if (selectedProduct.selectedProduct == 'SHOPPING_CART') return 'is-shopping-cart-product'
      return ''
    },
    formatNumber(value) {
      return Number(value)
    },
    toggleEditModal(open, product) {
      this.$emit('toggle-edit-modal', open, product)
    },
    removeFromSelected(deselectedProduct) {
      this.$emit('remove-from-selected', deselectedProduct)
    },
    partOfPackage(selectedProduct) {
      if (selectedProduct.PartOfPackage) {
        const product = this.selectedProducts.find((product) => {
          return product.OptionType_ID === selectedProduct.PartOfPackage
        })

        return product ? product : null
      }
      return null
    },
    isRemovableOrEditable(selectedProduct) {
      if (this.showEditAndDelete && !selectedProduct.Forced) return true
    },
  },
}
</script>

<style lang="scss" scoped>
:deep() {
  .row {
    grid-template-columns: auto 3em 6em 6em 3em;
    grid-template-areas:
      'title vat price-excl price-incl buttons'
      'packages packages packages packages packages';
  }

  .row-price-incl .row {
    grid-template-columns: auto 3em 6em 3em;
    grid-template-areas:
      'title vat price-incl buttons'
      'packages packages packages packages';
  }

  .row-price-excl .row {
    grid-template-columns: auto 3em 6em 3em;
    grid-template-areas:
      'title vat price-excl buttons'
      'packages packages packages packages';
  }
}

.packages {
  grid-area: packages;
}

button {
  margin-top: 0.1em;
}

button + button {
  margin-left: 0.5em;
  text-align: right;
}

.loader {
  font-size: 0.5em;
  transform: translateY(0.5em);
  margin-bottom: 0;
}
</style>
